import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

const WizardStepReview = ({ onBack, onAddToCart, isLoading, wizardData, buttonText }) => {
  const [totalCost, setTotalCost] = useState(null);
  const metalPrices = useSelector(state => state.metalPrices.prices);

  useEffect(() => {
    console.log('WizardStepReview - wizardData:', wizardData);
    console.log('WizardStepReview - metalPrices:', metalPrices);

    // Calculate total cost needed to reach target weight
    if (wizardData.target_weight && wizardData.weight_type && wizardData.metal_type && metalPrices) {
      const selectedMetal = metalPrices[wizardData.metal_type];
      console.log('WizardStepReview - selectedMetal:', selectedMetal);

      if (selectedMetal) {
        let pricePerUnit;

        // Get the appropriate price based on weight unit
        if (wizardData.weight_type === 'oz') {
          pricePerUnit = selectedMetal.price_per_ounce;
        } else if (wizardData.weight_type === 'g') {
          pricePerUnit = selectedMetal.price_per_gram;
        } else if (wizardData.weight_type === 'kg') {
          pricePerUnit = selectedMetal.price_per_gram * 1000; // Convert grams to kg
        }

        console.log('WizardStepReview - base pricePerUnit:', pricePerUnit);

        if (pricePerUnit > 0) {
          // Add 25% markup to the metal price
          const markupMultiplier = 1.25;
          const markedUpPricePerUnit = pricePerUnit * markupMultiplier;
          const cost = wizardData.target_weight * markedUpPricePerUnit;

          console.log('WizardStepReview - marked up pricePerUnit:', markedUpPricePerUnit);
          console.log('WizardStepReview - calculated cost with 25% markup:', cost);
          setTotalCost(cost);
        } else {
          console.log('WizardStepReview - pricePerUnit is 0 or invalid, setting to null');
          setTotalCost(null);
        }
      } else {
        console.log('WizardStepReview - selectedMetal not found, setting to null');
        setTotalCost(null);
      }
    } else {
      console.log('WizardStepReview - missing required data, setting to null');
      setTotalCost(null);
    }
  }, [wizardData, metalPrices]);

  const getWeightUnitDisplay = () => {
    const amount = wizardData.target_weight || 0;

    switch(wizardData.weight_type) {
      case 'g':
        return amount === 1 ? 'Gram' : 'Grams';
      case 'oz':
        return amount === 1 ? 'Troy Ounce' : 'Troy Ounces';
      case 'kg':
        return amount === 1 ? 'Kilogram' : 'Kilograms';
      default:
        return wizardData.weight_type;
    }
  };

  const getWeightUnitDisplayLower = () => {
    const amount = wizardData.target_weight || 0;

    switch(wizardData.weight_type) {
      case 'g':
        return amount === 1 ? 'gram' : 'grams';
      case 'oz':
        return amount === 1 ? 'troy ounce' : 'troy ounces';
      case 'kg':
        return amount === 1 ? 'kilogram' : 'kilograms';
      default:
        return wizardData.weight_type;
    }
  };

  const formatTotalCost = () => {
    if (totalCost === null || totalCost === undefined) {
      return 'N/A';
    }
    return `$${totalCost.toFixed(2)}`;
  };

  const metalName = wizardData.metal_type ? wizardData.metal_type.charAt(0).toUpperCase() + wizardData.metal_type.slice(1) : 'Gold';

  return (
    <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Step Description */}
        <h2 className="text-center text-2xl font-bold text-gray-800 mb-2">Goal Summary</h2>
        <p className="text-center text-gray-600 mb-4">Review your goal details before confirming.</p>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column - Summary Card */}
          <div className="bg-white rounded-2xl py-6 px-4 border-2 border-gray-200 shadow-xl">
            <div className="mb-4">
              <div className="flex justify-between items-center py-4 border-b border-gray-100">
                <div className="text-base text-gray-600 font-medium">Metal Type</div>
                <div className="text-lg font-semibold text-gray-800 text-right">
                  {metalName}
                </div>
              </div>

              <div className="flex justify-between items-center py-4 border-b border-gray-100">
                <div className="text-base text-gray-600 font-medium">Target Weight</div>
                <div className="text-lg font-semibold text-gray-800 text-right">
                  {wizardData.target_weight} {getWeightUnitDisplay()}
                </div>
              </div>

              <div className="flex justify-between items-center py-4 border-b border-gray-100">
                <div className="text-base text-gray-600 font-medium">Contribution Frequency</div>
                <div className="text-lg font-semibold text-gray-800 text-right">
                  {wizardData.period ? wizardData.period.charAt(0).toUpperCase() + wizardData.period.slice(1) : 'N/A'}
                </div>
              </div>

              <div className="flex justify-between items-center py-4 border-b border-gray-100">
                <div className="text-base text-gray-600 font-medium">Contribution Amount</div>
                <div className="text-lg font-semibold text-gray-800 text-right">
                  ${wizardData.amount_per_period}
                </div>
              </div>

              <div className="flex justify-between items-start py-4 bg-gradient-to-r from-blue-50 to-indigo-50 -mx-4 px-4 rounded-lg font-semibold mt-2">
                <div>
                  <div className="text-base text-gray-700">Estimated cost to delivery</div>
                  <div className="text-xs text-gray-500 font-normal">(as of {new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})</div>
                </div>
                <div className="text-lg text-gray-800 text-right">
                  {formatTotalCost()}
                </div>
              </div>
            </div>

            {totalCost !== null && (
              <div className="text-center mt-4">
                <p className="text-green-600 font-semibold text-lg">
                  Acquire your {metalName} in approx {(() => {
                    const paymentsPerMonth = wizardData.period === 'weekly' ? 4 : wizardData.period === 'bi-weekly' ? 2 : wizardData.period === 'monthly' ? 1 : 0.33;
                    const monthsNeeded = Math.ceil(totalCost / (wizardData.amount_per_period * paymentsPerMonth));
                    return monthsNeeded;
                  })()} month{(() => {
                    const paymentsPerMonth = wizardData.period === 'weekly' ? 4 : wizardData.period === 'bi-weekly' ? 2 : wizardData.period === 'monthly' ? 1 : 0.33;
                    const monthsNeeded = Math.ceil(totalCost / (wizardData.amount_per_period * paymentsPerMonth));
                    return monthsNeeded > 1 ? 's' : '';
                  })()}!
                </p>
              </div>
            )}

            {totalCost === null && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                <i className="bi bi-exclamation-triangle mr-2"></i>
                <small>Metal prices are currently unavailable. Total cost will be calculated when prices are loaded.</small>
              </div>
            )}
          </div>

          {/* Right Column - Info Content */}
          <div className="flex flex-col gap-4">
            {/* How it works */}
            <div className="bg-white rounded-xl p-5 pt-3 pb-4 border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-3">How your {metalName.toLowerCase()} goal works:</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Set your contribution amount and frequency. Your balance accumulates until it reaches the price of your target metal. Once you hit the threshold, we source your metal from APMEX — one of the most trusted names in precious metals — and ship it directly to you. Any extra balance credits toward your next goal. Simple savings, real physical metal in your hands.
              </p>
            </div>

            {/* What you'll receive */}
            <div className="bg-white rounded-xl p-5 pt-3 pb-4 border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-3">What you'll receive:</h4>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                Your {metalName.toLowerCase()} arrives in tamper-evident packaging with an authenticity assay card verifying weight, purity, and certification. Every bar is sourced from APMEX's inventory of trusted mints and refiners.
              </p>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>• Contains {wizardData.metal_type === 'gold' ? '.9999' : wizardData.metal_type === 'platinum' ? '.9995' : '.999'} fine {metalName.toLowerCase()}</li>
                <li>• Sourced from industry-leading suppliers</li>
                <li>• Real, physical {metalName.toLowerCase()} you own outright</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-between mt-4 max-w-5xl mx-auto">
          <button
            onClick={onBack}
            disabled={isLoading}
            className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Back
          </button>
          <button
            onClick={onAddToCart}
            disabled={isLoading}
            className={`px-6 py-2 rounded-full font-bold transition-colors min-w-[140px] ${
              isLoading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {isLoading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Processing...
              </>
            ) : (
              <>
                {buttonText || "Create Goal"}
                <i className="bi bi-check-circle ml-2"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepReview;
